/**
 * Appointments Tab - Enhanced UX Version
 *
 * Key improvements over previous version:
 * 1. Card-based list instead of dense table
 * 2. Clear visual hierarchy with upcoming vs past split
 * 3. Prominent action buttons
 * 4. Centralized status configuration
 * 5. Better spacing and typography
 * 6. Highlighted in-progress appointments
 */

import { useState, useEffect, useCallback } from 'react'
import { Card, List, Space, Typography, Empty, Spin } from 'antd'
import { CalendarOutlined, ClockCircleOutlined, VideoCameraOutlined, EditOutlined, UserOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import { ActionableListItem } from '../../common'
import { APPOINTMENT_STATUS, getStatusColor } from '../../../config/status.config'
import { LAYOUT } from '../../../config/design.config'

const { Title } = Typography

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3520/api'

interface Appointment {
  id: string
  scheduledAt: string
  duration: number
  status: string
  statusLabel: string
  type: string
  typeLabel: string
  roomUrl: string | null
  doctor: {
    id: string
    name: string
    image: string | null
  }
  workflowInstanceId: string | null
  workflowType: string | null
  isStandalone: boolean
  patientNotes: string | null
  doctorNotes: string | null
  createdAt: string
}

interface AppointmentsTabProps {
  patientId: string
}

export function AppointmentsTab({ patientId }: AppointmentsTabProps) {
  const [appointments, setAppointments] = useState<Appointment[]>([])
  const [loading, setLoading] = useState(true)

  const fetchAppointments = useCallback(async () => {
    setLoading(true)
    try {
      const token = localStorage.getItem('admin_access_token')
      const response = await fetch(`${API_URL}/admin/consultations/patient/${patientId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      if (response.ok) {
        const data = await response.json()
        setAppointments(data)
      }
    } catch (error) {
      console.error('Failed to fetch appointments:', error)
    } finally {
      setLoading(false)
    }
  }, [patientId])

  useEffect(() => {
    fetchAppointments()
  }, [fetchAppointments])

  // Split appointments into upcoming and past
  const now = dayjs()
  const upcoming = appointments.filter((apt) => dayjs(apt.scheduledAt).isAfter(now))
  const past = appointments.filter((apt) => dayjs(apt.scheduledAt).isBefore(now))

  if (loading) {
    return (
      <Card style={LAYOUT.card}>
        <div style={{ textAlign: 'center', padding: '48px 0' }}>
          <Spin tip="Loading appointments..." />
        </div>
      </Card>
    )
  }

  const isVideoConsultation = (type: string) => {
    return type === 'VIDEO_CONSULTATION' || type === 'INITIAL_CONSULTATION'
  }

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      {/* Upcoming Appointments Section */}
      <Card
        title={
          <Space>
            <CalendarOutlined />
            <Title level={4} style={{ margin: 0 }}>
              Upcoming Appointments
            </Title>
          </Space>
        }
      >
        {upcoming.length === 0 ? (
          <Empty description="No upcoming appointments" />
        ) : (
          <List
            dataSource={upcoming}
            renderItem={(apt) => (
              <ActionableListItem
                key={apt.id}
                title={apt.doctor.name}
                description={
                  <Space size="small">
                    {isVideoConsultation(apt.type) ? (
                      <VideoCameraOutlined style={{ color: '#1890ff' }} />
                    ) : (
                      <UserOutlined />
                    )}
                    <span>{apt.typeLabel}</span>
                  </Space>
                }
                avatar={{
                  src: apt.doctor.image || undefined,
                }}
                metadata={[
                  <>
                    <CalendarOutlined style={{ marginRight: 4 }} />
                    {dayjs(apt.scheduledAt).format('DD.MM.YYYY')}
                  </>,
                  <>
                    <ClockCircleOutlined style={{ marginRight: 4 }} />
                    {dayjs(apt.scheduledAt).format('HH:mm')}
                  </>,
                  `${apt.duration} min`,
                ]}
                tags={[
                  {
                    label: apt.statusLabel,
                    color: getStatusColor(APPOINTMENT_STATUS, apt.status),
                  },
                ]}
                actions={[
                  ...(apt.roomUrl && apt.status === 'IN_PROGRESS'
                    ? [
                        {
                          label: 'Join Call',
                          onClick: () => window.open(apt.roomUrl!, '_blank'),
                          type: 'primary' as const,
                          icon: <VideoCameraOutlined />,
                        },
                      ]
                    : []),
                  {
                    label: 'Reschedule',
                    onClick: () => {
                      // Handle reschedule - could open modal or navigate
                      console.log('Reschedule appointment:', apt.id)
                    },
                    icon: <EditOutlined />,
                  },
                ]}
                highlight={apt.status === 'IN_PROGRESS'}
              />
            )}
          />
        )}
      </Card>

      {/* Past Appointments Section */}
      <Card
        title={
          <Space>
            <ClockCircleOutlined />
            <Title level={4} style={{ margin: 0 }}>
              Past Appointments
            </Title>
          </Space>
        }
      >
        {past.length === 0 ? (
          <Empty description="No past appointments" />
        ) : (
          <List
            dataSource={past}
            renderItem={(apt) => (
              <ActionableListItem
                key={apt.id}
                title={apt.doctor.name}
                description={
                  <Space size="small">
                    {isVideoConsultation(apt.type) ? (
                      <VideoCameraOutlined style={{ color: '#1890ff' }} />
                    ) : (
                      <UserOutlined />
                    )}
                    <span>{apt.typeLabel}</span>
                  </Space>
                }
                avatar={{
                  src: apt.doctor.image || undefined,
                }}
                metadata={[
                  <>
                    <CalendarOutlined style={{ marginRight: 4 }} />
                    {dayjs(apt.scheduledAt).format('DD.MM.YYYY')}
                  </>,
                  <>
                    <ClockCircleOutlined style={{ marginRight: 4 }} />
                    {dayjs(apt.scheduledAt).format('HH:mm')}
                  </>,
                  `${apt.duration} min`,
                ]}
                tags={[
                  {
                    label: apt.statusLabel,
                    color: getStatusColor(APPOINTMENT_STATUS, apt.status),
                  },
                ]}
                actions={[
                  {
                    label: 'View Details',
                    onClick: () => {
                      // Navigate to appointment details
                      window.location.href = `/consultations/${apt.id}`
                    },
                    type: 'link' as const,
                  },
                ]}
              />
            )}
          />
        )}
      </Card>
    </Space>
  )
}
